/**
 * Phase 3.6 — RecommendationScorePanel
 *
 * Composite recommendation score = Σ wᵢ × termᵢ over four sub-terms:
 *   uplift, efficiency_norm, confidence_score, risk_penalty.
 *
 * Renders a stacked horizontal bar where each segment width is
 * proportional to (weight × value). Headline number is the composite
 * score with the recommendation_rank as a small badge.
 *
 * Defaults match the seeded admin settings (a=0.4, b=0.3, c=0.2,
 * d=0.1). Does not call any backend endpoint — every value comes from
 * the /diff payload.
 */
import { Scale } from "lucide-react";

const DEFAULT_WEIGHTS = { a: 0.4, b: 0.3, c: 0.2, d: 0.1 };

const TERMS = [
    {
        key: "uplift",
        label: "Uplift",
        weightKey: "a",
        colorVar: "var(--ux-success)",
        hint: "Pk gain over baseline (clipped to [0, 1]).",
    },
    {
        key: "efficiency_norm",
        label: "Efficiency",
        weightKey: "b",
        colorVar: "var(--ux-accent)",
        hint: "Pk gain per unit design cost (normalised to study max).",
    },
    {
        key: "confidence_score",
        label: "Confidence",
        weightKey: "c",
        colorVar: "var(--ux-info)",
        hint: "CI tightness × seed budget × model tier.",
    },
    {
        key: "risk_penalty",
        label: "Risk penalty",
        weightKey: "d",
        colorVar: "var(--ux-danger)",
        hint: "Weighted failure-mode mass. Subtracted from the score.",
    },
];

function fmt(n, digits = 3) {
    if (typeof n !== "number" || Number.isNaN(n)) return "—";
    return n.toFixed(digits);
}

export default function RecommendationScorePanel({ diff, weights = DEFAULT_WEIGHTS }) {
    const rec = diff?.recommended;
    if (!rec || typeof rec.recommendation_score !== "number") return null;

    const w = { ...DEFAULT_WEIGHTS, ...(weights || {}) };
    // Build segments: weighted contribution = weight × value (risk is
    // signed negative since it subtracts).
    const segments = TERMS.map((t) => {
        const raw = rec[t.key];
        const value = typeof raw === "number" ? raw : 0;
        const weight = w[t.weightKey] ?? 0;
        const signedContribution =
            t.key === "risk_penalty" ? -1 * weight * value : weight * value;
        return {
            ...t,
            value,
            weight,
            contribution: signedContribution,
            magnitude: Math.abs(signedContribution),
        };
    });

    const totalMagnitude = segments.reduce((s, x) => s + x.magnitude, 0) || 1;
    const rank = typeof rec.recommendation_rank === "number"
        ? rec.recommendation_rank
        : null;

    return (
        <div
            className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-4 space-y-3"
            data-testid="recommendation-score-panel"
        >
            <div className="flex items-center gap-2">
                <Scale className="w-4 h-4 text-ux-accent" />
                <h3
                    className="text-[13px] font-medium text-ux-text-1"
                    title="A balanced rank that combines absolute uplift, efficiency, statistical confidence, and operational risk."
                >
                    Composite recommendation score
                </h3>
                <span
                    className="ml-auto font-mono tabular-nums text-[18px] text-ux-text-1"
                    data-testid="recommendation-score-headline"
                >
                    {fmt(rec.recommendation_score, 3)}
                </span>
                {rank !== null && (
                    <span
                        className="inline-flex items-center text-[10px] uppercase tracking-wider text-ux-accent bg-ux-accent-soft px-2 py-0.5 rounded ml-1"
                        data-testid="recommendation-score-rank"
                    >
                        rank {rank}
                    </span>
                )}
            </div>

            <div
                className="flex h-3 rounded overflow-hidden border border-ux-border-1"
                data-testid="recommendation-score-bar"
            >
                {segments.map((s) => {
                    const widthPct = (s.magnitude / totalMagnitude) * 100;
                    if (widthPct < 0.5) return null;
                    return (
                        <div
                            key={s.key}
                            style={{ width: `${widthPct}%`, background: s.colorVar }}
                            title={`${s.label} (w=${s.weight}, value=${fmt(s.value)})`}
                            data-testid={`recommendation-score-segment-${s.key}`}
                        />
                    );
                })}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {segments.map((s) => (
                    <div
                        key={s.key}
                        className="rounded border border-ux-border-1 bg-ux-bg-2 px-2 py-1.5"
                        data-testid={`recommendation-score-tile-${s.key}`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-[10px] uppercase tracking-wider text-ux-text-3">
                                {s.label}
                            </span>
                            <span
                                className="text-[10px] font-mono text-ux-text-3"
                                title={`Weight (${s.weightKey})`}
                            >
                                w={s.weight}
                            </span>
                        </div>
                        <div className="text-[13px] font-mono tabular-nums text-ux-text-1 mt-0.5">
                            {fmt(s.value, 3)}
                        </div>
                        <div
                            className="text-[10px] font-mono text-ux-text-3"
                            title={s.hint}
                        >
                            {s.key === "risk_penalty" ? "− " : ""}
                            {fmt(Math.abs(s.contribution), 3)}
                        </div>
                    </div>
                ))}
            </div>

            <div
                className="text-[11px] text-ux-text-3 font-mono"
                data-testid="recommendation-score-weights"
            >
                weights: a={w.a} · b={w.b} · c={w.c} · d={w.d}
            </div>
        </div>
    );
}
